import { bound } from "@frui.ts/helpers";
import { watchBusy } from "@frui.ts/screens";
import { validate, validateAll } from "@frui.ts/validation";
import { KnownErrors, Modules } from "config";
import * as Sentry from "@sentry/react";
import ChargeResponse from "entities/chargeResponse";
import CustomerPartialRefundCandidatePreviewResponse from "entities/customerPartialRefundCandidatePreviewResponse";
import CustomerPartialRefundCandidateRequest, {
  ChangeFuelTypeRequestValidationRules,
} from "entities/customerPartialRefundCandidateRequest";
import RefundCounterpartyOverridability from "entities/refundCounterpartyOverridability";
import type { interfaces } from "inversify";
import cloneDeep from "lodash/cloneDeep";
import { computed, observable, reaction } from "mobx";
import ChargesRepository from "repositories/chargesRepository";
import EnumService from "services/enumService";
import EventBus, { chargeFuelTypeChanged, chargePartialRefundCandidateCreated } from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService, { NotificationType } from "services/notificationService";
import OptionsService from "services/optionsService";
import SecurityService from "services/securityService";
import { serializeAccountNumber } from "utils/filters";
import { translateGeneral } from "utils/localizations";
import AlertsViewModel from "../../../alertsViewModel";
import CountrySelectViewModel from "../../../countrySelectViewModel";
import { BaseRefundViewModel } from "./baseRefundViewModel";
import { formatCzechAccountNumber } from "@edaz/utils/bankAccount";
import RefundPaymentType from "entities/refundPaymentType";
import TransferPaymentType from "entities/transferPaymentType";
import CustomerRefundCandidatePreviewRefundPaymentResponse from "entities/customerRefundCandidatePreviewRefundPaymentResponse";
import PriceListService from "../../../../services/priceListService";
import type SearchChargesRequest from "entities/searchChargesRequest";
import FuelTypeChangePreviewResponse from "../../../../entities/fuelTypeChangePreviewResponse";
import CustomerPartialRefundCandidateRejectionReason from "entities/customerPartialRefundCandidateRejectionReason";

type FuelTypeChangeRefundError = {
  content?: {
    type: string;
  };
};

export default class FuelTypeChangeRefundViewModel extends BaseRefundViewModel<CustomerPartialRefundCandidateRequest> {
  readonly validationRules = ChangeFuelTypeRequestValidationRules;
  @observable previewPartialResponse?: CustomerPartialRefundCandidatePreviewResponse;
  @observable previewFuelTypeResponse?: FuelTypeChangePreviewResponse;
  @observable refundPayment?: CustomerRefundCandidatePreviewRefundPaymentResponse;
  private unsubscriptions: Array<() => void> = [];

  constructor(
    protected repository: ChargesRepository,
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected notificationService: NotificationService,
    protected optionsService: OptionsService,
    protected securityService: SecurityService,
    protected enumService: EnumService,
    protected countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>,
    public alertsViewModel: AlertsViewModel,
    public priceListService: PriceListService,
    public chargeResponse: ChargeResponse,
    public filter: SearchChargesRequest
  ) {
    super(
      repository,
      localizationService,
      eventBus,
      notificationService,
      optionsService,
      securityService,
      enumService,
      countrySelectViewModelFactory,
      alertsViewModel,
      chargeResponse
    );
    this.countrySelectViewModel = this.countrySelectViewModelFactory("id");
    this.unsubscriptions.push(reaction(() => [this.item?.fuelTypeId], this.isWithoutRefundFeeDefault));
  }

  protected loadDetail() {
    const entity = new CustomerPartialRefundCandidateRequest();
    this.attachValidators(entity);
    return entity;
  }

  @computed
  get secondStep() {
    return this.previewPartialResponse ?? this.previewFuelTypeResponse;
  }

  @computed
  get changeWithRefund() {
    return this.onlyPartialRefundationAllowed || this.isFuelTypeIdFromPartialRefundation;
  }

  @computed
  get isCzechLicensePlate() {
    const czechCountry = this.enumService.values("countries").find(country => country.code === "CZ")?.id;
    return czechCountry === this.chargeResponse.vehicle.countryId;
  }

  @bound
  isWithoutRefundFeeDefault() {
    const refundFree = this.changeWithRefund && this.isCzechLicensePlate;
    if (refundFree) {
      this.item.isWithoutRefundFee = true;
    }
  }

  @computed
  get onlyPartialRefundationAllowed() {
    return (
      this.chargeResponse.permittedActions.partialRefundation.isAllowed &&
      !this.chargeResponse.permittedActions.fuelTypeChange.isAllowed
    );
  }

  @computed
  get isFuelTypeIdFromPartialRefundation() {
    return (
      this.chargeResponse.permittedActions.partialRefundation.isAllowed &&
      this.chargeResponse.permittedActions.fuelTypeChange.isAllowed &&
      this.item?.fuelTypeId &&
      this.chargeResponse.permittedActions.partialRefundation.availableFuelTypeIds?.includes(this.item.fuelTypeId)
    );
  }

  @computed
  get saveButtonText() {
    if (this.secondStep && this.changeWithRefund) {
      return translateGeneral("confirm");
    }
    if (this.secondStep && this.item.emailAddress) {
      return translateGeneral("confirmAndSubmit");
    }
    return translateGeneral("continue");
  }

  @computed
  get paymentInformationRequired() {
    return (
      this.chargeResponse.permittedActions.partialRefundation.counterpartyOverridability ===
        RefundCounterpartyOverridability.Required &&
      this.chargeResponse.permittedActions.partialRefundation.isAllowed &&
      !!this.item?.fuelTypeId
    );
  }

  @computed
  get paymentInformationOptional() {
    return (
      this.chargeResponse.permittedActions.partialRefundation.counterpartyOverridability ===
        RefundCounterpartyOverridability.Allowed &&
      this.chargeResponse.permittedActions.partialRefundation.isAllowed &&
      !!this.item?.fuelTypeId
    );
  }

  @computed
  get paymentInformationDenied() {
    return (
      this.chargeResponse.permittedActions.partialRefundation.counterpartyOverridability ===
      RefundCounterpartyOverridability.Denied
    );
  }

  @computed
  get availablePartialRefundationIds() {
    return this.chargeResponse.permittedActions.partialRefundation.availableFuelTypeIds;
  }

  @computed
  get availableFuelTypeIds() {
    return this.chargeResponse.permittedActions.fuelTypeChange.availableFuelTypeIds;
  }

  @computed
  get fuelTypeOptions() {
    const partialRefundationIds = this.availablePartialRefundationIds ? this.availablePartialRefundationIds : [];
    const fuelTypeIds = this.availableFuelTypeIds ? this.availableFuelTypeIds : [];
    const availableIds = [...partialRefundationIds, ...fuelTypeIds];

    return this.optionsService.enumOptions("fuel_types").filter(fuel => availableIds?.includes(fuel.id));
  }

  @computed
  get newChargePrice() {
    return this.previewPartialResponse instanceof CustomerPartialRefundCandidatePreviewResponse
      ? this.previewPartialResponse.candidatePreview.newChargePrice
      : undefined;
  }

  @computed
  get informationText() {
    if (!this.previewPartialResponse) {
      return undefined;
    }

    const refundNegativeAmount = this.previewPartialResponse.candidatePreview.rawRefundAmount <= 0;

    let informationText = "";
    if (refundNegativeAmount) {
      informationText = this.translatePreview("refundNegativeAmount");
    } else {
      switch (this.previewPartialResponse.candidatePreview.refundPayment.paymentType) {
        case RefundPaymentType.DomesticTransfer:
          informationText = this.translatePreview("refundDomesticTransfer").replace(
            "%accountNumber%",
            formatCzechAccountNumber(
              this.previewPartialResponse.candidatePreview.refundPayment.transferPayment?.counterpartyBank?.bankAccount
            )
          );
          break;
        case RefundPaymentType.ForeignTransfer:
          informationText = this.translatePreview("refundForeignTransfer");
          break;
        case RefundPaymentType.SepaTransfer:
          informationText = this.translatePreview("refundSepaTransfer");
          break;
        case RefundPaymentType.PayGate:
          informationText = this.translatePreview("refundPayGate").replace(
            "%cardNumber%",
            this.previewPartialResponse.candidatePreview.refundPayment.payGatePayment?.cardNumber ?? ""
          );
          break;
      }
    }

    return informationText;
  }

  @computed
  get rejectedReasonText() {
    if (!this.previewPartialResponse?.rejectionReason && !this.previewFuelTypeResponse?.rejectionReason) {
      return undefined;
    }
    const reasonPartial = this.previewPartialResponse?.rejectionReason;

    if (reasonPartial === CustomerPartialRefundCandidateRejectionReason.UnreturnableRefund) {
      return this.translateFuelTypePreview(`rejectedReason.${reasonPartial}`);
    }
    if (reasonPartial === CustomerPartialRefundCandidateRejectionReason.FuelTypeNotAllowed) {
      return this.translatePreview("fuelTypeNotAllowed").replace("%FUEL_TYPE%", this.allowedFuelType ?? "");
    }
    return this.translatePreview("warningBypassingForbidden");
  }

  @computed
  get rejectedReason() {
    return !!this.rejectedReasonText;
  }

  @computed
  get warningReasons() {
    if (!this.previewPartialResponse?.warningReasons && !this.previewFuelTypeResponse?.warningReasons) {
      return undefined;
    }
    return this.previewPartialResponse?.warningReasons
      ? this.previewPartialResponse?.warningReasons
      : this.previewFuelTypeResponse?.warningReasons;
  }

  @computed
  get allowedFuelType() {
    if (!this.previewPartialResponse && !this.previewFuelTypeResponse) {
      return undefined;
    }
    const fuelTypeId = this.previewPartialResponse?.allowedFuelTypeId ?? this.previewFuelTypeResponse?.allowedFuelTypeId;
    const fuelType = this.enumService.values("fuel_types").find(fuelType => fuelType.id === fuelTypeId);

    if (!fuelType) {
      return;
    }
    return this.localizationService.translateEnum("fuelType", fuelType.fuelType);
  }

  @bound
  onSubmit() {
    if (this.secondStep) {
      this.onSave();
      return;
    }
    if (this.changeWithRefund && !this.secondStep) {
      this.previewRefund();
    } else {
      this.previewFuelChange();
    }
  }

  @bound
  onBackClick() {
    this.previewPartialResponse = undefined;
    this.previewFuelTypeResponse = undefined;
    if (this.refundPayment?.isTransferredToSepa && this.item.transferType) {
      this.item.transferType = TransferPaymentType.Sepa;
    }
  }

  @bound
  @watchBusy
  private async previewRefund() {
    const isValid = this.includePaymentOptions ? validateAll([this.item, this.item.counterpartyBank]) : validate(this.item);
    if (!isValid) {
      return;
    }

    try {
      const data = cloneDeep(this.item);
      if (this.includePaymentOptions) {
        data.counterpartyBank.accountNumber = serializeAccountNumber(this.item.counterpartyBank.accountNumber);
      }
      if (this.chargeResponse.emailAddress || !data.emailAddress) {
        data.emailAddress = undefined;
      }
      if (data.counterpartyBank?.bankAccount && !data.counterpartyBank?.bankAccount?.bank) {
        data.counterpartyBank.bankAccount.bank = undefined;
      }
      const response = await this.repository.fuelTypeChangeRefundPreview(this.chargeResponse.chargeId, data);
      this.previewPartialResponse = response;
      this.refundPayment = response.candidatePreview.refundPayment;
    } catch (e) {
      const type = (e as FuelTypeChangeRefundError).content?.type;
      if (type === KnownErrors.REFUND_COUNTERPARTY_OVERRIDE_AFTER_SEPA_TRANSFER_INVALID) {
        this.item.transferType = TransferPaymentType.Sepa;
      }
      Sentry.captureException(e);
    }
  }

  @bound
  @watchBusy
  private async previewFuelChange() {
    const isValid = validate(this.item);
    if (!isValid) {
      return;
    }

    try {
      const data = cloneDeep(this.item);
      if (this.chargeResponse.emailAddress || !data.emailAddress) {
        data.emailAddress = undefined;
      }
      this.previewFuelTypeResponse = await this.repository.fuelTypeChangePreview(this.chargeResponse.chargeId, data);
    } catch (e) {
      Sentry.captureException(e);
    }
  }

  @bound
  private priceListItem(charge: ChargeResponse) {
    return this.priceListService.priceListItemById(charge.priceListItemId);
  }

  @bound
  translateFuelType(charge: ChargeResponse) {
    const priceItem = this.priceListItem(charge);
    if (!priceItem) {
      return;
    }

    const fuelType = this.enumService.values("fuel_types").find(fuelType => fuelType.id === priceItem.fuelTypeId);
    if (!fuelType) {
      return;
    }
    return this.localizationService.translateEnum("fuelType", fuelType.fuelType);
  }

  @bound
  translateFuelTypeId(id: string) {
    const fuelType = this.enumService.values("fuel_types").find(fuelType => fuelType.id === id);
    if (!fuelType) {
      return;
    }
    return this.localizationService.translateEnum("fuelType", fuelType.fuelType);
  }

  @bound
  translateChargeType(charge: ChargeResponse) {
    const priceItem = this.priceListItem(charge);
    if (!priceItem) {
      return;
    }

    const chargeType = this.enumService.values("charge_types").find(chargeType => chargeType.id === priceItem.chargeTypeId);
    if (!chargeType) {
      return;
    }
    return this.localizationService.translateEnum("chargeValidity", chargeType.validFor);
  }

  @bound
  @watchBusy
  // eslint-disable-next-line sonarjs/cognitive-complexity
  async onSave() {
    const isValid = this.validate();

    if (isValid) {
      const data = cloneDeep(this.item);
      if (this.chargeResponse.emailAddress || !data.emailAddress) {
        data.emailAddress = undefined;
      }
      if (data.counterpartyBank?.bankAccount && !data.counterpartyBank?.bankAccount?.bank) {
        data.counterpartyBank.bankAccount.bank = undefined;
      }
      if (
        this.changeWithRefund &&
        this.includePaymentOptions &&
        data.counterpartyBank &&
        data.counterpartyBank.bankAccount &&
        this.item.counterpartyBank
      ) {
        data.counterpartyBank.bankAccount.accountNumber =
          serializeAccountNumber(this.item.counterpartyBank.bankAccount?.accountNumber) ?? "";
      }
      let entity;

      if (this.changeWithRefund) {
        if (this.refundPayment?.isTransferredToSepa && this.refundPayment?.transferPayment) {
          const transferredData = { ...data, ...this.refundPayment.transferPayment };
          transferredData.transferType = TransferPaymentType.Sepa;
          await this.repository.fuelTypeChangeRefund(this.chargeResponse.chargeId, transferredData);
        } else {
          await this.repository.fuelTypeChangeRefund(this.chargeResponse.chargeId, data);
        }
        const charge = await this.repository.getCharge(this.filter, this.chargeResponse.chargeId);
        entity = { ...charge.items[0] };
        this.eventBus.publish(chargePartialRefundCandidateCreated({ entity }));
      } else {
        await this.repository.fuelTypeUpdate(this.chargeResponse.chargeId, data);
        const charge = await this.repository.getCharge(this.filter, this.chargeResponse.chargeId);
        entity = { ...charge.items[0] };
      }
      this.eventBus.publish(chargeFuelTypeChanged({ entity }));
      this.notificationService.add({
        title: this.translate(this.changeWithRefund ? "fuelTypeChangedWithRefund" : "fuelTypeChanged"),
        type: NotificationType.SUCCESS,
      });
      this.requestClose();
    }
  }

  @bound
  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.callCenter, `fuelTypeChange.${code}`, size);
  }

  translatePreview(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.callCenter, `refundPreview.${code}`, size);
  }
  translateFuelTypePreview(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.callCenter, `refundFuelTypePreview.${code}`, size);
  }

  translateGeneral(code: string, size?: number) {
    return this.localizationService.translateGeneral(code, size);
  }

  translateCountryName(countryId: string) {
    return this.enumService.value("countries", countryId)?.name ?? "";
  }

  static Factory({ container }: interfaces.Context) {
    return (chargeResponse: ChargeResponse, filter: SearchChargesRequest) =>
      new FuelTypeChangeRefundViewModel(
        container.get(ChargesRepository),
        container.get(LocalizationService),
        container.get(EventBus),
        container.get(NotificationService),
        container.get(OptionsService),
        container.get(SecurityService),
        container.get(EnumService),
        container.get(CountrySelectViewModel.Factory),
        container.get(AlertsViewModel),
        container.get(PriceListService),
        chargeResponse,
        filter
      );
  }
}
